import { useState, useCallback } from 'react';
import useSWRMutation from 'swr/mutation';
import { message } from 'antd';
import { apiService } from '../services/api';
import { useI18n } from '../i18n';
import { deleteAccountSWRConfig } from './useSWRConfig';

interface SendVerificationParams {
  email: string;
}

interface DeleteAccountParams {
  email: string;
  code: string;
}

interface DeleteAccountResponse {
  success: boolean;
  message?: string;
  error?: string;
}

// 发送验证码的fetcher
const sendVerificationFetcher = async (url: string, { arg }: { arg: SendVerificationParams }) => {
  return await apiService.sendDeleteVerificationCode(arg.email);
};

// 删除账户的fetcher
const deleteAccountFetcher = async (url: string, { arg }: { arg: DeleteAccountParams }) => {
  return await apiService.deleteAccount(arg.email, arg.code);
};

export const useDeleteAccountSWR = () => {
  const { t } = useI18n();
  const [verificationSent, setVerificationSent] = useState(false);

  // 发送验证码的mutation
  const {
    trigger: sendVerificationTrigger,
    isMutating: isSendingVerification,
    error: sendVerificationError,
  } = useSWRMutation(
    '/auth/send-delete-verification',
    sendVerificationFetcher,
    {
      ...deleteAccountSWRConfig,
      onSuccess: (data) => {
        if (data.success) {
          message.success(t('deleteAccount.verification.sent'));
          setVerificationSent(true);
        } else {
          const errorMsg = data.message || t('deleteAccount.verification.sendFailed');
          message.error(errorMsg);
        }
      },
      onError: (error) => {
        console.error('Send verification code error:', error);
        message.error(t('deleteAccount.verification.sendFailed'));
      },
    }
  );

  // 重新发送验证码的mutation
  const {
    trigger: resendVerificationTrigger,
    isMutating: isResendingVerification,
    error: resendVerificationError,
  } = useSWRMutation(
    '/auth/send-delete-verification',
    sendVerificationFetcher,
    {
      ...deleteAccountSWRConfig,
      onSuccess: (data) => {
        if (data.success) {
          message.success(t('deleteAccount.verification.resent'));
        } else {
          const errorMsg = data.message || t('deleteAccount.verification.sendFailed');
          message.error(errorMsg);
        }
      },
      onError: (error) => {
        console.error('Resend verification code error:', error);
        message.error(t('deleteAccount.verification.sendFailed'));
      },
    }
  );

  // 删除账户的mutation
  const {
    trigger: deleteAccountTrigger,
    isMutating: isDeletingAccount,
    error: deleteAccountError,
  } = useSWRMutation(
    '/auth/delete-account',
    deleteAccountFetcher,
    {
      ...deleteAccountSWRConfig,
      onSuccess: (data) => {
        if (data.success) {
          message.success(t('deleteAccount.delete.success'));
        } else {
          const errorMsg = data.message || t('deleteAccount.delete.failed');
          message.error(errorMsg);
        }
      },
      onError: (error) => {
        console.error('Delete account error:', error);
        message.error(t('deleteAccount.delete.failed'));
      },
    }
  );

  // 发送验证码
  const sendVerificationCode = useCallback(async (params: SendVerificationParams): Promise<DeleteAccountResponse> => {
    try {
      const result = await sendVerificationTrigger(params);
      return {
        success: result.success,
        message: result.success ? t('deleteAccount.verification.sent') : undefined,
        error: result.success ? undefined : (result.message || t('deleteAccount.verification.sendFailed')),
      };
    } catch (error) {
      return {
        success: false,
        error: t('deleteAccount.verification.sendFailed'),
      };
    }
  }, [sendVerificationTrigger, t]);

  // 重新发送验证码
  const resendVerificationCode = useCallback(async (params: SendVerificationParams): Promise<DeleteAccountResponse> => {
    try {
      const result = await resendVerificationTrigger(params);
      return {
        success: result.success,
        message: result.success ? t('deleteAccount.verification.resent') : undefined,
        error: result.success ? undefined : (result.message || t('deleteAccount.verification.sendFailed')),
      };
    } catch (error) {
      return {
        success: false,
        error: t('deleteAccount.verification.sendFailed'),
      };
    }
  }, [resendVerificationTrigger, t]);

  // 删除账户
  const deleteAccount = useCallback(async (params: DeleteAccountParams): Promise<DeleteAccountResponse> => {
    try {
      const result = await deleteAccountTrigger(params);
      return {
        success: result.success,
        message: result.success ? t('deleteAccount.delete.success') : undefined,
        error: result.success ? undefined : (result.message || t('deleteAccount.delete.failed')),
      };
    } catch (error) {
      return {
        success: false,
        error: t('deleteAccount.delete.failed'),
      };
    }
  }, [deleteAccountTrigger, t]);

  // 重置验证状态
  const resetVerificationState = useCallback(() => {
    setVerificationSent(false);
  }, []);

  return {
    // 状态
    verificationSent,
    loading: isSendingVerification || isResendingVerification || isDeletingAccount,
    
    // 方法
    sendVerificationCode,
    resendVerificationCode,
    deleteAccount,
    resetVerificationState,
    
    // 错误状态
    errors: {
      sendVerification: sendVerificationError,
      resendVerification: resendVerificationError,
      deleteAccount: deleteAccountError,
    },
  };
};
